import "./style.css";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header, { forgetAccessBadge } from './Header';
import { ArrowLeft } from 'lucide-react';
// Same origin, like every other screen: nginx proxies /api and /auth to the backend on both
// ports, and an absolute http:// URL is blocked as mixed content the moment the site answers
// on https (2026-09-02). See backendConfig.js.
import { BACKEND_URL } from './backendConfig';
import { useTranslation, Trans } from 'react-i18next';
// A partner company's own track (DAC VN, 2026-10-18) — staging only, with the rest of it (index.js).
const PARTNER_ON = process.env.REACT_APP_PARTNER === '1';



function Register() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    /* ARRIVING FROM AN ACCESS REQUEST — Jay Jung's "admin review/EDIT/approve", 2026-10-07. The
       details come in on the query string and are EDITABLE like any other: what the person typed
       about themselves is a starting point, not a record. The request id rides along so the row
       can be marked once the account actually exists — marking it on the way IN would leave a
       request reading "approved" for an account somebody abandoned halfway. */
    const [params] = useSearchParams();
    const fromRequest = params.get('request');

    const [values, setValues] = useState({
        username: "", firstName: "", lastName: "", email: "",
        isActive: true, sendWelcomeEmail: true, externalOrgKey: "", entityKey: "",
    });
    /* WHICH ENTITY, set when the account is made (Max, 28/10: "thêm trường entity vào nhé") rather than afterwards in
       the user list — the same catalogue and the same server rule as the edit form; optional, as it is there. */
    const [entities, setEntities] = useState([]);
    useEffect(() => {
        axios.get(`${BACKEND_URL}/api/admin/entities`, { withCredentials: true })
            .then((r) => setEntities((r.data.entities || []).filter((en) => en.isActive))).catch(() => setEntities([]));
    }, []);
    /* A PARTNER COMPANY'S ACCOUNT — Max, 2026-10-18: "tạo cho account DAC mà ko có email thì cần gắn cờ".
       With a partner picked the email may stay empty; then there is nowhere to send a welcome mail, so the
       server starts the account on the first-login password and the screen says so. */
    const [partnerOrgs, setPartnerOrgs] = useState([]);
    useEffect(() => {
        if (!PARTNER_ON) return;
        axios.get(`${BACKEND_URL}/api/admin/external-orgs`, { withCredentials: true })
            .then((r) => setPartnerOrgs((r.data.orgs || []).filter((o) => o.active))).catch(() => setPartnerOrgs([]));
    }, []);
    const partner = !!values.externalOrgKey;
    const noMail = partner && !values.email.trim();

    /* Filled in once, on arrival. Not a controlled mirror of the URL: the admin is expected to
       correct what a stranger typed, and re-applying the query string on every render would undo
       the correction as they made it. */
    useEffect(() => {
        if (!fromRequest) return;
        setValues((prev) => ({
            ...prev,
            email: params.get('email') || prev.email,
            username: params.get('username') || prev.username,
            firstName: params.get('firstName') || prev.firstName,
            lastName: params.get('lastName') || prev.lastName,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromRequest]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [roleCatalog, setRoleCatalog] = useState([]);
    const [msSearch, setMsSearch] = useState('');
    const [backendError, setBackendError] = useState("");
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        axios.get(`${BACKEND_URL}/api/admin/role-options`)
            .then((res) => setRoleCatalog(res.data.roles || []))
            // the names people read (Jay 02/11) — "SOP Team" is the key of "S.OP Staff"
            .catch(() => setRoleCatalog([{ roleKey: 'User', roleName: 'User' }, { roleKey: 'Admin', roleName: 'Admin' }, { roleKey: 'SOP Team', roleName: 'S.OP Staff' }]));
    }, []);

    const setField = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));
    /* Picking a partner ticks the partner's role — the account opens nothing without it. It stays a tick
       the admin can see and undo, not something done behind the form. */
    const setPartner = (key) => {
        setField('externalOrgKey', key);
        if (key && roleCatalog.some((r) => r.roleKey === 'External Vendor')) {
            setSelectedRoles((prev) => (prev.includes('External Vendor') ? prev : [...prev, 'External Vendor']));
        }
    };
    const toggleRole = (key) => setSelectedRoles((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

    const validateForm = () => {
        const errors = [];
        if (!values.username.trim()) errors.push(t('xUsers.err.usernameRequired'));
        else if (values.username.length < 3 || values.username.length > 50) errors.push(t('xUsers.err.usernameLen'));
        if (!values.firstName.trim() || values.firstName.length < 2) errors.push(t('xUsers.err.firstMin'));
        // Optional for a partner company's person (Max, 2026-10-19 — DAC's people come as addresses, not full names).
        if (!partner && (!values.lastName.trim() || values.lastName.length < 2)) errors.push(t('xUsers.err.lastMin'));
        // A partner company's account may have no email; anybody else's must have a valid one.
        if (!noMail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.push(t('xUsers.err.emailStar'));
        if (!selectedRoles.length) errors.push(t('xUsers.err.roleStar'));
        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true); setBackendError(""); setSuccessMessage("");
        try {
            const made = await axios.post(`${BACKEND_URL}/register`, {
                username: values.username.trim(), firstName: values.firstName.trim(), lastName: values.lastName.trim(),
                email: values.email.trim(), roles: selectedRoles, role: selectedRoles[0], isActive: values.isActive ? 1 : 0,
                ...(PARTNER_ON ? { externalOrgKey: values.externalOrgKey || '' } : {}),
                entityKey: values.entityKey || '',
            });
            if (made.data?.firstPassword) {
                setSuccessMessage(t('xUsers.reg.createdNoMail', { name: values.username.trim() }));
            } else if (values.sendWelcomeEmail) {
                await axios.post(`${BACKEND_URL}/send-welcome-email`, { email: values.email, username: values.username });
                setSuccessMessage(t('xUsers.reg.createdMailed'));
            } else {
                setSuccessMessage(t('xUsers.reg.created'));
            }
            /* NOW the request is marked, not on the way in: a row reading "approved" for an
               account somebody abandoned halfway would be worse than one still saying pending.
               The username goes with it so the two can be put side by side later.
               Its own try: the account exists either way, and a failure here is a row to tidy,
               not a reason to tell the admin their user was not created. */
            if (fromRequest) {
                try {
                    await axios.post(
                        `${BACKEND_URL}/api/access-requests/${fromRequest}/decide`,
                        { decision: 'approved', username: values.username.trim() },
                        { withCredentials: true },
                    );
                    forgetAccessBadge();
                } catch (e) {
                    setSuccessMessage((m) => t('xUsers.reg.requestNotMarked', { msg: m }));
                }
            }
            // Long enough to read the password line when there is one.
            setTimeout(() => navigate(fromRequest ? "/admin/access-requests" : "/list-user"), noMail ? 6000 : 1500);
        } catch (err) {
            if (err.response?.status === 400 && err.response.data?.details) {
                setValidationErrors(err.response.data.details.map((d) => d.message));
            } else if (err.response?.status === 400 && err.response.data?.error) {
                setBackendError(`*${err.response.data.error}`);
            } else if (err.response?.status === 409) {
                setBackendError(err.response.data.error === "Username already exists"
                    ? t('xUsers.err.usernameTaken')
                    : t('xUsers.err.emailTaken'));
            } else {
                setBackendError(t('xUsers.err.unexpected'));
            }
        } finally { setLoading(false); }
    };

    const q = msSearch.trim().toLowerCase();
    const filteredRoles = q ? roleCatalog.filter((o) => o.roleName.toLowerCase().includes(q)) : roleCatalog;

    return (
        <div className="reg-page">
            <Header />
            <div className="reg-shell">
                <form className="reg-card" onSubmit={handleSubmit}>
                    <div className="reg-head">
                        <h1>{t('xUsers.reg.title')}</h1>
                        <button type="button" className="reg-btn ghost" onClick={() => navigate('/list-user')}><ArrowLeft size={16} /> {t('xUsers.reg.back')}</button>
                    </div>

                    {successMessage && <div className="reg-alert success">{successMessage}</div>}
                    {backendError && <div className="reg-alert error">{backendError}</div>}
                    {validationErrors.length > 0 && (
                        <div className="reg-alert error">{validationErrors.map((er, i) => <div key={i}>{er}</div>)}</div>
                    )}

                    <div className="reg-two-col">
                        <div className="reg-col-left">
                            <label>{t('xUsers.f.username')}<input value={values.username} onChange={(e) => setField('username', e.target.value)} placeholder={t('xUsers.reg.usernamePh')} /></label>
                            {PARTNER_ON && (
                                <label>{t('xUsers.f.partnerCompany')}
                                    <select value={values.externalOrgKey} onChange={(e) => setPartner(e.target.value)}>
                                        <option value="">{t('xUsers.f.noPartner')}</option>
                                        {partnerOrgs.map((o) => <option key={o.orgKey} value={o.orgKey}>{o.orgName}</option>)}
                                    </select>
                                </label>
                            )}
                            <label>{t('xUsers.f.entity')}
                                <select value={values.entityKey} onChange={(e) => setField('entityKey', e.target.value)} data-reg-entity>
                                    <option value="">{t('xUsers.edit.none')}</option>
                                    {['helinox', 'vendor'].map((kind) => (
                                        <optgroup key={kind} label={kind === 'helinox' ? 'Helinox' : t('xUsers.ent.kindVendor')}>
                                            {entities.filter((en) => en.kind === kind).map((en) => (
                                                <option key={en.key} value={en.key}>{en.key}{en.name !== en.key ? ` — ${en.name}` : ''}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </label>
                            <label>{partner ? t('xUsers.f.emailOptional') : t('xUsers.f.email')}<input type="email" value={values.email} onChange={(e) => setField('email', e.target.value)} placeholder={partner ? t('xUsers.reg.emailPhPartner') : 'name@helinox.com'} /></label>
                            {noMail && <div className="reg-hint"><Trans t={t} i18nKey="xUsers.reg.noMailHint" components={{ b: <b /> }} /></div>}
                            <label>{t('xUsers.f.firstName')}<input value={values.firstName} onChange={(e) => setField('firstName', e.target.value)} /></label>
                            <label>{partner ? t('xUsers.f.lastNameOptional') : t('xUsers.f.lastName')}<input value={values.lastName} onChange={(e) => setField('lastName', e.target.value)} /></label>
                            <label className="reg-check"><input type="checkbox" checked={values.isActive} onChange={(e) => setField('isActive', e.target.checked)} /> {t('xUsers.f.active')}</label>
                            {!noMail && <label className="reg-check"><input type="checkbox" checked={values.sendWelcomeEmail} onChange={(e) => setField('sendWelcomeEmail', e.target.checked)} /> {t('xUsers.reg.sendWelcome')}</label>}
                        </div>
                        <div className="reg-col-right">
                            <span className="reg-label">{t('xUsers.f.roles')} <span className="reg-role-count">{t('xUsers.f.rolesSelected', { n: selectedRoles.length })}</span></span>
                            <input className="reg-search" placeholder={t('xUsers.f.searchRoles')} value={msSearch} onChange={(e) => setMsSearch(e.target.value)} />
                            <div className="reg-roles-list">
                                {filteredRoles.map((o) => (
                                    <label key={o.roleKey} className={`reg-role-row${selectedRoles.includes(o.roleKey) ? ' on' : ''}`}>
                                        <input type="checkbox" checked={selectedRoles.includes(o.roleKey)} onChange={() => toggleRole(o.roleKey)} />
                                        {o.roleName}
                                    </label>
                                ))}
                                {!filteredRoles.length && <div className="reg-empty">{t('xUsers.f.noRoles')}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="reg-foot">
                        <button type="submit" className="reg-btn primary" disabled={loading}>{loading ? t('xUsers.reg.creating') : t('xUsers.reg.create')}</button>
                        <button type="button" className="reg-btn ghost" onClick={() => navigate('/list-user')}>{t('xUsers.reg.cancel')}</button>
                    </div>
                </form>
            </div>

            <style jsx="true">{`
                .reg-page { background: #F4F6F9; min-height: 100vh; }
                .reg-shell { margin-top: 64px; padding: 24px; transition: margin-left 180ms ease, width 180ms ease, max-width 180ms ease; }
                @media (min-width: 900px) {
                    body.portal-shell.portal-sidebar-open .reg-shell {
                        margin-left: 270px; width: calc(100% - 270px); max-width: calc(100% - 270px);
                    }
                }
                .reg-card { background: #fff; border: 1px solid #D8E0EC; border-radius: 14px; max-width: 760px; margin: 0 auto; padding: 24px 28px; box-shadow: 0 1px 4px rgba(31,56,100,.06); }
                .reg-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
                .reg-head h1 { font-size: 22px; font-weight: 900; color: #1F3864; margin: 0; }

                .reg-alert { border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; margin-bottom: 14px; }
                .reg-alert.success { background: #ECF6F1; border: 1px solid #C9E7DA; color: #17604A; }
                .reg-alert.error { background: #FDECEC; border: 1px solid #F5C2C2; color: #B42318; }

                .reg-two-col { display: grid; grid-template-columns: 1fr 300px; gap: 22px; }
                @media (max-width: 680px) { .reg-two-col { grid-template-columns: 1fr; } }
                .reg-col-left { display: flex; flex-direction: column; gap: 14px; }
                .reg-col-left label { display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; font-weight: 700; color: #374151; }
                .reg-col-left input:not([type=checkbox]) { border: 1px solid #D8E0EC; border-radius: 8px; padding: 9px 11px; font-size: 14px; font-weight: 500; color: #111827; }
                .reg-col-left input:not([type=checkbox]):focus { outline: none; border-color: #2E75B6; }
                .reg-check { flex-direction: row !important; align-items: center; gap: 8px; font-size: 14px !important; font-weight: 600 !important; }
                .reg-check input { width: 16px; height: 16px; }
                .reg-col-left select { border: 1px solid #D8E0EC; border-radius: 8px; padding: 9px 11px; font-size: 14px; font-weight: 500; color: #111827; background: #fff; }
                .reg-hint { font-size: 12.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px; padding: 8px 11px; line-height: 1.45; }

                .reg-col-right { display: flex; flex-direction: column; }
                .reg-label { font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 8px; }
                .reg-role-count { font-weight: 600; color: #6B7280; font-size: 11.5px; }
                .reg-search { box-sizing: border-box; border: 1px solid #E5EAF2; border-radius: 7px; padding: 8px 10px; font-size: 13px; }
                .reg-search:focus { outline: none; border-color: #2E75B6; }
                .reg-roles-list { margin-top: 6px; border: 1px solid #E5EAF2; border-radius: 8px; padding: 4px; max-height: 260px; overflow-y: auto; flex: 1; }
                .reg-role-row { display: flex; align-items: center; gap: 9px; font-size: 13.5px; font-weight: 600; color: #334155; padding: 8px 9px; border-radius: 6px; cursor: pointer; }
                .reg-role-row:hover { background: #F3F6FB; }
                .reg-role-row.on { background: #EAF0F8; color: #1F3864; }
                .reg-role-row input { width: 15px; height: 15px; }
                .reg-empty { padding: 10px; font-size: 13px; color: #9CA3AF; }

                .reg-foot { display: flex; gap: 10px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #EEF2F7; }
                .reg-btn { display: inline-flex; align-items: center; gap: 7px; border-radius: 8px; padding: 9px 18px; font-size: 14px; font-weight: 700; cursor: pointer; border: 1px solid #D8E0EC; background: #fff; color: #1F3864; }
                .reg-btn.primary { background: #1F3864; color: #fff; border-color: #1F3864; }
                .reg-btn:disabled { opacity: .6; cursor: not-allowed; }
            `}</style>
        </div>
    );
}

export default Register;
